import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getColors } from '../theme';
import { cleanName } from './assistantName';

// Settings > Assistant memory: everything the assistant has been told about
// the user, as ruled rows they can edit or delete. It reads the same fields
// the assistant does (users/{uid}.agentProfile, and `why` on habits and goals).

const MOTIVATIONS = {
  build: 'Build better habits',
  break: 'Break bad habits',
  track: 'Track my routines',
  goals: 'Achieve specific goals',
  explore: 'Just exploring',
};

const TONES = [
  { value: 'gentle', label: 'Gentle' },
  { value: 'direct', label: 'Direct' },
  { value: 'tough', label: 'Tough' },
];

const Label = ({ children, colors }) => (
  <span className="font-mono text-[11px] pt-3 sm:pt-[15px]" style={{ color: colors.textMuted }}>{children}</span>
);

const textButton = 'font-mono text-xs underline underline-offset-4 min-h-[32px] whitespace-nowrap';

// One line of memory: text you can click to edit, and a delete action
const MemoryLine = ({ value, placeholder, onSave, onDelete, colors, multiline = false }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  const save = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next !== (value || '')) await onSave(next);
  };

  if (editing) {
    const Field = multiline ? 'textarea' : 'input';
    return (
      <div className="flex items-start gap-3 py-2">
        <Field
          autoFocus
          value={draft}
          rows={multiline ? 3 : undefined}
          maxLength={multiline ? 1000 : 200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
            if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          }}
          className="flex-1 min-w-0 bg-transparent outline-none text-[16px] py-1.5 resize-none"
          style={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.textPrimary}` }}
        />
        <button type="button" onClick={save} className="px-3 min-h-[36px] text-xs font-medium" style={{ backgroundColor: colors.accent, color: colors.onAccent }}>
          Save
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <button
        type="button"
        onClick={() => { setDraft(value || ''); setEditing(true); }}
        className="text-left text-[15px] min-w-0 break-words hover:underline underline-offset-4"
        style={{ color: value ? colors.textPrimary : colors.textMuted }}
      >
        {value || placeholder}
      </button>
      {value && onDelete && (
        <button type="button" onClick={onDelete} className={textButton} style={{ color: colors.textSecondary, textDecorationColor: colors.borderStrong }}>
          Forget
        </button>
      )}
    </div>
  );
};

// A list field (what keeps them going, what derails them): one row per item
const MemoryList = ({ items, placeholder, onChange, colors }) => {
  const [adding, setAdding] = useState('');
  const add = () => {
    const next = adding.trim();
    if (!next) return;
    onChange([...(items || []), next]);
    setAdding('');
  };
  return (
    <div>
      {(items || []).map((item, i) => (
        <MemoryLine
          key={`${i}-${item}`}
          value={item}
          colors={colors}
          onSave={(next) => onChange(next ? items.map((x, j) => (j === i ? next : x)) : items.filter((_, j) => j !== i))}
          onDelete={() => onChange(items.filter((_, j) => j !== i))}
        />
      ))}
      <input
        value={adding}
        onChange={(e) => setAdding(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onBlur={add}
        maxLength={200}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full bg-transparent outline-none text-[15px] py-2.5"
        style={{ color: colors.textPrimary, '--placeholder': colors.textMuted }}
      />
    </div>
  );
};

const AssistantMemory = () => {
  const { isDark, agentProfile, updateUserData, habits, goals, updateHabit, updateGoal } = useApp();
  const { isPro } = useSubscription();
  const colors = getColors(isDark);
  const [confirmForget, setConfirmForget] = useState(false);

  if (!isPro) return null;

  const profile = agentProfile || {};
  // Send the whole profile with the change, so an edit never depends on how the write merges.
  // Clearing writes an empty value rather than removing the key.
  const save = (fields) => updateUserData({ agentProfile: { ...profile, ...fields } });

  const habitReasons = habits.filter((h) => h.why);
  const goalReasons = goals.filter((g) => g.why);
  const isEmpty = !profile.motivation && !profile.why && !profile.motivators?.length && !profile.obstacles?.length &&
    !profile.tone && !profile.notes && habitReasons.length === 0 && goalReasons.length === 0;

  // One write for everything: separate updateHabit calls would each start from
  // the same stale list and undo each other
  const forgetAll = async () => {
    await updateUserData({
      // Its name isn't something it knows about you, so it stays
      agentProfile: { motivation: '', why: '', motivators: [], obstacles: [], tone: '', notes: '', assistantName: profile.assistantName || '' },
      habits: habits.map((h) => (h.why ? { ...h, why: '' } : h)),
      goals: goals.map((g) => (g.why ? { ...g, why: '' } : g)),
    });
    setConfirmForget(false);
  };

  const rule = { borderBottom: `1px solid ${colors.border}` };
  const grid = 'grid grid-cols-1 sm:grid-cols-[130px_minmax(0,1fr)] gap-x-4';

  return (
    <section className="pt-5 pb-2 mb-6" style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
      <h3 className="text-[15px] mb-2 font-semibold tracking-tight" style={{ color: colors.textPrimary }}>
        Assistant memory
      </h3>
      <p className="text-sm mb-4" style={{ color: colors.textSecondary }}>
        What the assistant knows about you and uses to keep you going. Click any line to change it.
      </p>

      <div style={{ borderTop: `1px solid ${colors.border}` }}>
        <div className={grid} style={rule}>
          <Label colors={colors}>Its name</Label>
          <MemoryLine
            value={profile.assistantName}
            placeholder="Give it a name"
            colors={colors}
            onSave={(next) => save({ assistantName: cleanName(next) })}
            onDelete={() => save({ assistantName: '' })}
          />
        </div>
        <div className={grid} style={rule}>
          <Label colors={colors}>Came here to</Label>
          <MemoryLine
            value={MOTIVATIONS[profile.motivation] || profile.motivation}
            placeholder="Not saved"
            colors={colors}
            onSave={(next) => save({ motivation: next })}
            onDelete={() => save({ motivation: '' })}
          />
        </div>
        <div className={grid} style={rule}>
          <Label colors={colors}>Your why</Label>
          <MemoryLine
            value={profile.why}
            placeholder="Why you're doing this"
            multiline
            colors={colors}
            onSave={(next) => save({ why: next })}
            onDelete={() => save({ why: '' })}
          />
        </div>
        <div className={grid} style={rule}>
          <Label colors={colors}>Keeps you going</Label>
          <MemoryList items={profile.motivators} placeholder="Add something…" colors={colors} onChange={(motivators) => save({ motivators })} />
        </div>
        <div className={grid} style={rule}>
          <Label colors={colors}>Derails you</Label>
          <MemoryList items={profile.obstacles} placeholder="Add something…" colors={colors} onChange={(obstacles) => save({ obstacles })} />
        </div>
        <div className={grid} style={rule}>
          <Label colors={colors}>Tone</Label>
          <div className="flex flex-wrap gap-2 py-2.5" role="radiogroup" aria-label="How the assistant talks to you">
            {TONES.map((t) => {
              const on = (profile.tone || 'direct') === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => save({ tone: t.value })}
                  className="px-3 min-h-[36px] text-sm"
                  style={on
                    ? { backgroundColor: colors.accent, color: colors.onAccent }
                    : { border: `1px solid ${colors.borderStrong}`, color: colors.textPrimary }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={grid} style={rule}>
          <Label colors={colors}>Notes</Label>
          <MemoryLine
            value={profile.notes}
            placeholder="Anything else it should remember"
            multiline
            colors={colors}
            onSave={(next) => save({ notes: next })}
            onDelete={() => save({ notes: '' })}
          />
        </div>
        {[...habitReasons.map((h) => ({ key: h.id, label: h.name, why: h.why, update: (why) => updateHabit(h.id, { why }) })),
          ...goalReasons.map((g) => ({ key: g.id, label: g.title, why: g.why, update: (why) => updateGoal(g.id, { why }) }))]
          .map((item) => (
            <div key={item.key} className={grid} style={rule}>
              <Label colors={colors}><span className="block truncate" title={item.label}>{item.label}</span></Label>
              <MemoryLine value={item.why} colors={colors} onSave={item.update} onDelete={() => item.update('')} />
            </div>
          ))}
      </div>

      {!isEmpty && (
        <div className="pt-3">
          {confirmForget ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm" style={{ color: colors.textPrimary }}>Forget everything above?</span>
              <button type="button" onClick={forgetAll} className="px-3 min-h-[36px] text-sm font-medium" style={{ backgroundColor: colors.error, color: colors.onAccent }}>
                Forget all
              </button>
              <button type="button" onClick={() => setConfirmForget(false)} className="px-3 min-h-[36px] text-sm" style={{ border: `1px solid ${colors.borderStrong}`, color: colors.textPrimary }}>
                Keep
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmForget(true)} className={textButton} style={{ color: colors.textSecondary, textDecorationColor: colors.borderStrong }}>
              Forget everything
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default AssistantMemory;
